import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import Button from "./Button";

function CreateUniverseDialog({ open, onClose, onCreated }) {
	const [universeId, setUniverseId] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");

	const handleCancel = useCallback((event) => {
		event.preventDefault();
		onClose();
	}, [onClose]);

	const handleSubmit = useCallback(async () => {
		if (!universeId.trim()) {
			setError("universeId is required");
			return;
		}
		setIsSubmitting(true);
		setError("");

		try {
			const token = localStorage.getItem("token");
			const response = await fetch("/v1/admin/universes", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({
					universeId: universeId.trim(),
					name: name.trim() || null,
					description: description.trim() || null,
				}),
			});

			if (response.ok) {
				if (onCreated) onCreated();
				onClose();
			} else {
				const msg = await response.text();
				setError(msg || "Errore durante la creazione");
			}
		} catch (err) {
			setError("Errore di connessione");
		} finally {
			setIsSubmitting(false);
		}
	}, [universeId, name, description, onCreated, onClose]);

	React.useEffect(() => {
		if (!open) {
			setUniverseId("");
			setName("");
			setDescription("");
			setError("");
		}
	}, [open]);

	if (!open) return null;

	return (
		<dialog
			open
			className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4"
			aria-modal="true"
			aria-labelledby="create-universe-title"
			tabIndex={-1}
			onCancel={handleCancel}
		>
			<div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
				<div className="border-b border-slate-200 px-5 py-4">
					<h2 id="create-universe-title" className="text-lg font-semibold text-slate-900">
						Create Universe
					</h2>
					<p className="mt-1 text-sm text-slate-600">
						Crea un nuovo universo per organizzare le security
					</p>
				</div>

				<div className="space-y-4 px-5 py-4">
					<div>
						<label htmlFor="cu-universeId" className="block text-sm font-medium text-slate-700">
							Universe ID *
						</label>
						<input
							id="cu-universeId"
							type="text"
							value={universeId}
							onChange={(e) => setUniverseId(e.target.value)}
							className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
							required
						/>
					</div>
					<div>
						<label htmlFor="cu-name" className="block text-sm font-medium text-slate-700">
							Name
						</label>
						<input
							id="cu-name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
						/>
					</div>
					<div>
						<label htmlFor="cu-description" className="block text-sm font-medium text-slate-700">
							Description
						</label>
						<textarea
							id="cu-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
							className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
						/>
					</div>

					{error && (
						<div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
							{error}
						</div>
					)}
				</div>

				<div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Creating..." : "Create"}
					</Button>
				</div>
			</div>
		</dialog>
	);
}

CreateUniverseDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onCreated: PropTypes.func,
};

export default CreateUniverseDialog;
